import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PenLine } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { LiveNowBanner } from "@/components/LiveNowBanner";
import { Footer } from "@/components/Footer";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const Journal = () => {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/posts/blog", { params: { limit: 30 } })
      .then((r) => setPosts(r.data || []))
      .finally(() => setLoading(false));
  }, []);

  const hero = posts[0];
  const rest = posts.slice(1);

  return (
    <div data-testid="journal-page" className="app-shell">
      <Navbar />
      <LiveNowBanner />
      <div className="container-belle" style={{ paddingTop: 110, paddingBottom: 64 }}>
        <div style={{ marginBottom: 40, maxWidth: 800 }}>
          <div className="overline" style={{ color: "var(--brand-accent)" }}>The Letters</div>
          <h1 className="editorial-h1" style={{ fontSize: "clamp(40px, 5.5vw, 64px)", marginTop: 12 }}>
            Letters<br />on living beautifully.
          </h1>
          <p className="editorial-body" style={{ fontSize: 17, marginTop: 20, color: "var(--text-secondary)", fontStyle: "italic" }}>
            A long-form archive — reflections, recipes, and small notes on French living. Anyone in the salon may write one.
          </p>
          {user && (
            <Link
              to="/compose?type=blog"
              data-testid="journal-write-cta"
              className="btn-primary"
              style={{
                display: "inline-flex", alignItems: "center", gap: 10, marginTop: 24, textDecoration: "none",
                background: "var(--brand-accent)",
              }}
            >
              <PenLine size={16} strokeWidth={1.6} />
              Write a letter
            </Link>
          )}
        </div>

        {loading && <p style={{ color: "var(--text-muted)" }}>Loading…</p>}

        {hero && (
          <Link to={`/post/${hero.id}`} data-testid={`journal-hero-${hero.id}`} style={{ display: "grid", gridTemplateColumns: "1.1fr 1fr", gap: 48, marginBottom: 80, alignItems: "center" }} className="journal-hero">
            <div className="media-zoom" style={{ overflow: "hidden", aspectRatio: "4/5" }}>
              <img loading="lazy" decoding="async" src={hero.media_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            </div>
            <div>
              <div className="overline" style={{ color: "var(--brand-accent)" }}>Featured letter</div>
              <h2 className="editorial-h1" style={{ fontSize: "clamp(32px, 4.5vw, 52px)", marginTop: 16, marginBottom: 20 }}>
                {hero.title}
              </h2>
              <p className="editorial-body" style={{ fontSize: 17, marginBottom: 24 }}>
                {hero.body?.slice(0, 220)}…
              </p>
              <div className="overline">By {hero.author?.display_name} · {hero.author?.location}</div>
              <div style={{ marginTop: 24, fontSize: 12, letterSpacing: "0.2em", textTransform: "uppercase", color: "var(--brand-accent)" }} className="link-underline">
                Read the letter →
              </div>
            </div>
          </Link>
        )}

        <div className="divider-line" style={{ marginBottom: 48 }} />

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 48 }}>
          {rest.map((p) => (
            <Link key={p.id} to={`/post/${p.id}`} data-testid={`journal-card-${p.id}`} className="card-belle-hover" style={{ display: "block" }}>
              {p.media_url && (
                <div className="media-zoom" style={{ aspectRatio: "5/4", overflow: "hidden", marginBottom: 16 }}>
                  <img loading="lazy" decoding="async" src={p.media_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                </div>
              )}
              <div className="overline">By {p.author?.display_name}</div>
              <h3 className="font-serif" style={{ fontSize: 26, lineHeight: 1.2, marginTop: 8, marginBottom: 10, fontWeight: 500 }}>
                {p.title}
              </h3>
              <p className="editorial-body" style={{ fontSize: 15, margin: 0 }}>
                {p.body?.slice(0, 140)}…
              </p>
            </Link>
          ))}
          {!loading && posts.length === 0 && (
            <div style={{ padding: 60, border: "1px solid var(--border-default)", textAlign: "center", gridColumn: "1 / -1" }}>
              <p className="font-serif" style={{ fontStyle: "italic", fontSize: 22 }}>The letters await their first author.</p>
              <Link to="/compose" className="btn-primary" style={{ display: "inline-block", marginTop: 16, textDecoration: "none" }}>Write the first letter</Link>
            </div>
          )}
        </div>
      </div>
      <Footer />
      <style>{`
        @media (max-width: 768px) {
          .journal-hero { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
};

export default Journal;
